import React from 'react'
import InputMask from 'react-input-mask'
import { Button, Form, Input, Typography } from 'antd'
import { AxiosError } from 'axios'
import { LoginOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons'
import { authApi } from 'api/auth'
import {
  ELoginTypes,
  IPhoneLoginTokensResponse,
  isPhoneLoginEmailStep
} from 'models/auth'
import {
  getContactCodeCooldownSeconds,
  setContactCodeCooldown
} from 'shared/utils/contactCodeCooldown'
import { normalizePhone } from 'shared/utils/normalizePhone'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './SignIn.module.scss'
import type { GetRef } from 'antd'

const CODE_LENGTH = 4

type TStep = 'phone' | 'sms' | 'email'

interface IPhoneForm {
  phone: string
}

interface ISignInPhoneProps {
  onSuccess: (data: IPhoneLoginTokensResponse) => void
  onSwitchToEmail: () => void
}

export const SignInPhone: React.FC<ISignInPhoneProps> = ({
  onSuccess,
  onSwitchToEmail
}) => {
  const [step, setStep] = React.useState<TStep>('phone')
  const [phone, setPhone] = React.useState(0)
  const [phoneLoginToken, setPhoneLoginToken] = React.useState('')
  const [isEmailRequired, setIsEmailRequired] = React.useState(false)
  const [emailMask, setEmailMask] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [timer, setTimer] = React.useState(0)
  // ремонтируем поля кода, чтобы очистить их после неверной попытки
  const [codeFieldKey, setCodeFieldKey] = React.useState(0)
  const codeRef = React.useRef<GetRef<typeof Input.OTP>>(null)

  // у SMS и письма свои паузы на повторную отправку
  const cooldownKey = step === 'email' ? `${phone}:email` : `${phone}`

  // на шаге почты код уходит только после того, как её укажут
  const isCodeSent = step === 'sms' || !isEmailRequired || Boolean(emailMask)

  React.useEffect(() => {
    if (step === 'phone' || !isCodeSent) return

    // Input.OTP не пробрасывает inputMode в ячейки, поэтому проставляем сами:
    // без него на телефоне открывается буквенная клавиатура
    codeRef.current?.nativeElement
      .querySelectorAll('input')
      .forEach((input) => {
        input.inputMode = 'numeric'
      })
    codeRef.current?.focus()
  }, [step, isCodeSent, codeFieldKey])

  React.useEffect(() => {
    if (step === 'phone') return

    setTimer(getContactCodeCooldownSeconds(cooldownKey))
    const interval = setInterval(
      () => setTimer(getContactCodeCooldownSeconds(cooldownKey)),
      1000
    )
    return () => clearInterval(interval)
  }, [step, cooldownKey])

  const goToPhoneStep = () => {
    setStep('phone')
    setError('')
    setPhoneLoginToken('')
    setEmailMask('')
    setIsEmailRequired(false)
    setEmail('')
  }

  const getErrorMessage = (e: unknown) =>
    (e as AxiosError<{ message: string }>).response?.data?.message

  const sendCode = async ({ phone: phoneValue }: IPhoneForm) => {
    const normalizedPhone = normalizePhone(phoneValue)

    try {
      setIsLoading(true)
      const { data } = await authApi.requestPhoneLogin({
        phone: normalizedPhone,
        type: ELoginTypes.SELLER
      })
      if (data.phone_verification_code_sent !== false) {
        setContactCodeCooldown(normalizedPhone)
      }
      setPhone(normalizedPhone)
      setPhoneLoginToken(data.phone_login_token)
      setError('')
      setCodeFieldKey((key) => key + 1)
      setStep('sms')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const verifySmsCode = async (code: string) => {
    try {
      setIsLoading(true)
      setError('')
      const { data } = await authApi.verifyPhoneLogin({
        phone_login_token: phoneLoginToken,
        code: Number(code)
      })

      if (!isPhoneLoginEmailStep(data)) {
        return onSuccess(data)
      }

      // номер записан в аккаунте, но не подтверждён: до входа просим код с почты
      setPhoneLoginToken(data.phone_login_token)
      setIsEmailRequired(data.email_required)
      setEmailMask(data.email_mask ?? '')
      if (data.email_code_sent) {
        setContactCodeCooldown(`${phone}:email`)
      }
      setCodeFieldKey((key) => key + 1)
      setStep('email')
    } catch (e) {
      setError(getErrorMessage(e) ?? 'Неверный код подтверждения')
      setCodeFieldKey((key) => key + 1)
    } finally {
      setIsLoading(false)
    }
  }

  const verifyEmailCode = async (code: string) => {
    if (isEmailRequired && !email.trim()) {
      setError('Укажите почту, указанную в аккаунте')
      setCodeFieldKey((key) => key + 1)
      return
    }

    try {
      setIsLoading(true)
      setError('')
      const { data } = await authApi.verifyPhoneLoginEmailCode({
        phone_login_token: phoneLoginToken,
        code: Number(code),
        email: isEmailRequired ? email.trim() : undefined
      })
      onSuccess(data)
    } catch (e) {
      setError(getErrorMessage(e) ?? 'Неверный код подтверждения')
      setCodeFieldKey((key) => key + 1)
    } finally {
      setIsLoading(false)
    }
  }

  const resendSmsCode = async () => {
    try {
      await authApi.resendPhoneLoginCode({
        phone_login_token: phoneLoginToken
      })
      setContactCodeCooldown(phone)
      setTimer(getContactCodeCooldownSeconds(phone))
      setError('')
      setCodeFieldKey((key) => key + 1)
      showMessage('success', 'Код отправлен повторно', '')
    } catch (e) {
      const message = getErrorMessage(e)
      // код уже отправлен, а вкладка об этом не знает — заводим таймер по ответу
      if (message?.includes('секунд')) {
        setContactCodeCooldown(phone)
        setTimer(getContactCodeCooldownSeconds(phone))
      }
      showServerError(e)
    }
  }

  const resendEmailCode = async () => {
    if (isEmailRequired && !email.trim()) {
      setError('Укажите почту, указанную в аккаунте')
      return
    }

    const emailCooldownKey = `${phone}:email`

    try {
      const { data } = await authApi.requestPhoneLoginEmailCode({
        phone_login_token: phoneLoginToken,
        email: isEmailRequired ? email.trim() : undefined
      })
      setPhoneLoginToken(data.phone_login_token)
      setEmailMask(data.email_mask)
      setContactCodeCooldown(emailCooldownKey)
      setTimer(getContactCodeCooldownSeconds(emailCooldownKey))
      setError('')
      setCodeFieldKey((key) => key + 1)
      showMessage('success', 'Код отправлен повторно', '')
    } catch (e) {
      const message = getErrorMessage(e)
      if (message?.includes('секунд')) {
        setContactCodeCooldown(emailCooldownKey)
        setTimer(getContactCodeCooldownSeconds(emailCooldownKey))
      }
      showServerError(e)
    }
  }

  if (step === 'phone') {
    return (
      <Form name='signInPhone' size='large' onFinish={sendCode}>
        <Form.Item
          name='phone'
          rules={[
            {
              required: true,
              message: 'Заполните номер телефона'
            },
            {
              min: 18,
              message: 'Заполните номер телефона'
            }
          ]}
        >
          <InputMask mask='+7 (999) 999-99-99' maskChar={null}>
            {/* @ts-ignore */}
            {() => (
              <Input
                placeholder='Номер телефона'
                prefix={<PhoneOutlined className='icon' />}
                autoComplete='tel'
              />
            )}
          </InputMask>
        </Form.Item>
        <Button
          block
          htmlType='submit'
          type='primary'
          icon={<LoginOutlined />}
          className={styles.signInButton}
          loading={isLoading}
        >
          Получить код
        </Button>
        <Button
          block
          icon={<MailOutlined />}
          className={styles.switchMethodButton}
          onClick={onSwitchToEmail}
        >
          Войти по Email
        </Button>
      </Form>
    )
  }

  const isSmsStep = step === 'sms'

  const codeHint = () => {
    if (isSmsStep) {
      return `Код подтверждения отправлен по SMS на номер ${phoneMaskStatic(
        phone
      )}. Код действует 10 минут.`
    }
    if (isEmailRequired) {
      return 'Номер записан в нескольких аккаунтах. Укажите почту аккаунта — на неё придёт код.'
    }
    return `Код подтверждения отправлен на почту ${emailMask}. Код действует 10 минут.`
  }

  return (
    <div className={styles.codeStep}>
      <Typography.Text>{codeHint()}</Typography.Text>
      {!isSmsStep && isEmailRequired && (
        <Input
          size='large'
          placeholder='Электронный адрес'
          prefix={<MailOutlined className='icon' />}
          value={email}
          autoComplete='email'
          autoCapitalize='none'
          autoCorrect='off'
          onChange={(e) => setEmail(e.target.value)}
        />
      )}
      <Input.OTP
        key={codeFieldKey}
        ref={codeRef}
        length={CODE_LENGTH}
        disabled={isLoading}
        status={error ? 'error' : undefined}
        formatter={(value) => value.replace(/\D/g, '')}
        onChange={isSmsStep ? verifySmsCode : verifyEmailCode}
      />
      {error && <Typography.Text type='danger'>{error}</Typography.Text>}
      <Typography.Link
        disabled={timer > 0 || isLoading}
        onClick={isSmsStep ? resendSmsCode : resendEmailCode}
      >
        {timer > 0
          ? `Отправить код повторно можно через ${timer} сек`
          : 'Отправить код повторно'}
      </Typography.Link>
      <Button block size='large' onClick={goToPhoneStep}>
        Изменить номер
      </Button>
    </div>
  )
}
